import { css } from '@emotion/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import { getFlatCourseAliases } from 'redux/selectors/course';
import NvFilePicker from 'shared/components/nv-filepicker';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import useUploadFile from 'shared/hooks/use-upload-file';
import { useAppDispatch } from 'redux/store';
import { LecturePage, NLecturePage } from 'redux/schemas/models/lecture-page';
import { setLectureHeaderIsSaving, updateLecturePage } from 'redux/actions/lecture-pages';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import LectureHeaderColorPicker from './lecture-header-color-picker';
import { LecturePageEditContentParams } from '../lecture-page-content';

type Props = {
  visible: boolean,
} & LecturePageEditContentParams;

export const LectureContentEditHeaderButton = (props: Props) => {
  const {
    visible,
    ...restProps
  } = props;

  const dispatch = useAppDispatch();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, props.catalogId));
  const { showTitle } = props.lecturePage;
  const { saveLecturePageData, setIsHoverOnHeader } = props;
  const [isEditingColor, setIsEditingColor] = useState(false);

  const { uploadFiles, isUploading, filesUploading } = useUploadFile();

  const styles = css`
    display: ${visible ? 'block' : 'none'};

    .icon-edit {
      font-size: 14px;
    }

    .color-picker-panel {
      position: absolute;
      left: 0;
      top: 0;
    }
  `;

  const onImagePicked = useCallback(async (files: File[]) => {
    // Display the 'Saving... Please wait' UI
    dispatch(setLectureHeaderIsSaving(true));

    const [novoEdFile] = await uploadFiles(files, S3NameSpaces.COVER_IMAGES);

    saveLecturePageData({
      coverImage: {
        fileName: novoEdFile.name,
        fileSize: novoEdFile.size,
        fileType: novoEdFile.type,
        uniqueId: novoEdFile.uniqueId,
      },
      viewOptions: {
        ...props.lecturePage.viewOptions,
        // Delete the background color set since this is mutually exclusive with background photos
        backgroundColor: null,
      },
    }).then((response: any) => {
      // TODO: This probably deserves more investigation
      /* This mimics what the Angularjs app does when uploading a new photo: waits until the initial
      photo upload request finishes, and then fires a 2nd request to update the lecture page data with the
      same response object as returned in the first request. We do not get the coverImageUrl after the first
      requrest because the save to S3 happens asynchronously (I believe), whereas it seems to always be available
      after this second request.

      Let's test & confirm this, but surely there's a better way? */
      const lecturePage = response.payload as NLecturePage;
      dispatch(updateLecturePage({
        catalogId: response.meta.arg.catalogId,
        lecturePageId: lecturePage.id,
        lecturePage,
      })).finally(() => {
        dispatch(setLectureHeaderIsSaving(false));
      });
    }).catch(() => {
      dispatch(setLectureHeaderIsSaving(false));
    });
  }, [saveLecturePageData]);

  const getItems = useCallback((newShowTitle: boolean): NvDropdownOption[] => {
    if (newShowTitle) {
      return [
        { type: 'text', text: t.LECTURE_PAGES.ADMIN.HEADER.EDIT_HEADER.COLOR(), callback: () => setIsEditingColor(true) },
        {
          type: 'custom',
          customItem: (
            <NvFilePicker accept={['image/*']} onChange={(files) => onImagePicked(files)}>
              <div className='bs4-dropdown-item'>{ t.LECTURE_PAGES.ADMIN.HEADER.EDIT_HEADER.IMAGE() }</div>
            </NvFilePicker>
          ),
        },
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.HEADER.EDIT_HEADER.HIDE_HEADER(courseAliases),
          callback: () => { saveLecturePageData({ showTitle: false }); setIsHoverOnHeader(false); },
        },
      ];
    }
    return [
      {
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.HEADER.EDIT_HEADER.DISPLAY_HEADER(courseAliases),
        callback: () => saveLecturePageData({ showTitle: true }),
      },
    ];
  }, [courseAliases, onImagePicked, saveLecturePageData, setIsHoverOnHeader]);

  const [items, setItems] = useState<NvDropdownOption[]>(getItems(showTitle));

  useEffect(() => {
    setItems(getItems(showTitle));
  }, [showTitle]);

  const ref = useRef(null);

  return (
    <div css={styles} ref={ref}>
      {isEditingColor && (
      <div className='color-picker-panel'>
        <LectureHeaderColorPicker {...restProps} closeColorPicker={() => setIsEditingColor(false)} />
      </div>
      )}
      {!isEditingColor
      && (
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        items={items}
        customTarget={() => (
          <div className='d-flex align-items-center ml-2'>
            <NvIcon icon='edit' size='sm' />
            <div className='ml-2'>{t.LECTURE_PAGES.ADMIN.HEADER.EDIT_HEADER.BUTTON()}</div>
          </div>
        )}
        // Disabling when the upload occurs causes the dropdown to close. Otherwise the NvFilePicker seems
        // to steal the click event and causes it to remain open
        disabled={isUploading}
        offset={10}
        minWidth={220}
        onToggle={(showList) => {
          if (showList) {
            hideAddUI(dispatch);
          }
        }}
      />
      )}
    </div>
  );
};

export default LectureContentEditHeaderButton;
